/* Ronini site-wide scroll reveal: fades each <main> section up as it enters the viewport.
   Skips the first section, anything already visible at load, and sections with their own motion. */
(() => {
  if (window.rnM) return;
  window.rnM = 1;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;

  const main = document.querySelector('main');
  if (!main) return;

  const SKIP = /hero|scrolling[-_]image|trust[-_]bar|marquee|main[-_]product|product[-_]gallery|buybox|announcement|password/i;
  const OWN_MOTION = '[data-rn],[data-rn-words],[data-rn-px],[data-no-motion]';
  const vh = window.innerHeight;
  const groups = new Map();

  const done = (el) => {
    el.classList.remove('rn-m', 'rn-m-in');
    el.style.removeProperty('--rn-md');
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const section = entry.target;
      const passed = entry.boundingClientRect.bottom < 0;
      if (!entry.isIntersecting && !passed) return;
      io.unobserve(section);
      const items = groups.get(section) || [];
      groups.delete(section);
      items.forEach((el, i) => {
        if (passed) return done(el);
        el.style.setProperty('--rn-md', `${Math.min(i, 4) * 90}ms`);
        el.classList.add('rn-m-in');
        setTimeout(done, 1000 + Math.min(i, 4) * 90, el);
      });
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  [...main.querySelectorAll(':scope > .shopify-section')].forEach((section, index) => {
    if (index === 0 || SKIP.test(section.id) || SKIP.test(section.className)) return;
    if (section.querySelector(OWN_MOTION)) return;
    if (section.getBoundingClientRect().top < vh * 0.92) return;

    const items = [...section.children].filter((el) => {
      if (/^(STYLE|SCRIPT|TEMPLATE|LINK|DIALOG)$/.test(el.tagName)) return false;
      const cs = getComputedStyle(el);
      return cs.display !== 'none' && cs.position !== 'fixed' && cs.position !== 'sticky' && el.offsetHeight > 0;
    });
    if (!items.length) return;

    items.forEach((el) => el.classList.add('rn-m'));
    groups.set(section, items);
    io.observe(section);
  });

  /* IntersectionObserver never reports a section that goes from below the viewport to above it without
     intersecting (End key, anchor links, restored scroll), so anything already scrolled past is shown here. */
  if (!groups.size) return;
  let ticking = 0;
  const sweep = () => {
    ticking = 0;
    groups.forEach((items, section) => {
      if (section.getBoundingClientRect().bottom >= 0) return;
      io.unobserve(section);
      groups.delete(section);
      items.forEach(done);
    });
    if (!groups.size) removeEventListener('scroll', onScroll);
  };
  const onScroll = () => { if (!ticking) ticking = requestAnimationFrame(sweep); };
  addEventListener('scroll', onScroll, { passive: true });
})();

/* Micro-interactions (2026-10-04, owner: "高级且丝滑，不影响用户体验，不影响加载速度").
   No libraries, no layout changes: Web Animations on opacity / translate / scale only, started
   by user input or a change on the page, never on load. Off for reduced motion. */
(() => {
  if (window.rnUX) return;
  window.rnUX = 1;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || !Element.prototype.animate) return;
  const EASE = 'cubic-bezier(.2, .7, .2, 1)';

  /* (Lazy-image fade-in removed 10-04: images now paint as soon as they arrive, no placeholder flash.) */

  /* 2. Prices that change while you choose (PDP buy box) roll in instead of snapping. */
  const lastText = new WeakMap();
  const tick = (el) => {
    const text = el.textContent.trim();
    if (lastText.get(el) === text) return;
    const had = lastText.has(el);
    lastText.set(el, text);
    if (had && el.offsetParent) el.animate({ opacity: [0.2, 1], translate: ['0 5px', '0 0'] }, { duration: 260, easing: EASE });
  };
  const PRICE = '[id^="ro-sizeprice"], [id^="ro-subprice-"], [id^="ro-oneprice-"], [id^="ro-subwas-"], [id^="ro-duoprice-"], [id^="ro-stickyprice-"]';
  const priceRoots = document.querySelectorAll('.ronini-bb-wrap');
  priceRoots.forEach((root) => {
    root.querySelectorAll(PRICE).forEach((el) => lastText.set(el, el.textContent.trim()));
    let queued = new Set();
    let frame = 0;
    new MutationObserver((records) => {
      records.forEach((r) => {
        const node = r.target.nodeType === 3 ? r.target.parentElement : r.target;
        const el = node && node.closest(PRICE);
        if (el) queued.add(el);
      });
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; queued.forEach(tick); queued = new Set(); });
    }).observe(root, { subtree: true, childList: true, characterData: true });
  });

  /* 3. The header cart count gives a small bump when it changes (the header script rewrites its text). */
  document.querySelectorAll('.header__cart-count-text').forEach((el) => {
    let last = el.textContent.trim();
    new MutationObserver(() => {
      const text = el.textContent.trim();
      if (text === last) return;
      last = text;
      if (el.offsetParent) el.animate({ scale: [1, 1.3, 1] }, { duration: 420, easing: EASE });
    }).observe(el, { childList: true, characterData: true, subtree: true });
  });

  /* 4. Cards press in slightly under a finger or click, so a tap feels answered. */
  const CARD = '.ronini-promo__card.is-linked, .rn-acard, .rs-card, .specs__card, .ronini-mega__card, .safety-comp__cell, .rs-ccard__tile';
  let pressed = null;
  const release = () => {
    if (!pressed) return;
    const el = pressed;
    pressed = null;
    el.animate({ scale: [0.985, 1] }, { duration: 260, easing: EASE });
  };
  document.addEventListener('pointerdown', (event) => {
    if (event.button > 0) return;
    // 10-06 (owner: hard to swipe card rows on iPhone): animating the card under a finger as a swipe starts made iOS
    // drop the horizontal gesture. Touch gets a CSS :active press instead (rn-motion.css); mouse/pen keep this.
    if (event.pointerType === 'touch') return;
    const el = event.target.closest(CARD);
    const control = event.target.closest('button, input, select, textarea, label');
    if (!el || (control && control !== el && el.contains(control))) return;
    pressed = el;
    el.animate({ scale: [1, 0.985] }, { duration: 140, easing: 'ease-out', fill: 'forwards' });
  }, { passive: true });
  ['pointerup', 'pointercancel', 'dragstart'].forEach((type) => document.addEventListener(type, () => {
    if (pressed) pressed.getAnimations().forEach((a) => { if (a.effect && a.effect.getKeyframes().some((k) => k.scale)) a.cancel(); });
    release();
  }, { passive: true }));
})();

/* Header shadow once the page has scrolled (CSS in rn-motion.css). */
(() => {
  let queued = false;
  const root = document.documentElement;
  const update = () => { queued = false; root.classList.toggle('rn-scrolled', window.scrollY > 8); };
  addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(update); } }, { passive: true });
  update();
})();

/* Feeding 101 lives on the page's alternate template (?view=feeding-101). Menu links set in the admin point at the
   bare page URL, so they get the view added (no effect once the page itself is assigned that template). */
(() => {
  document.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href');
    if (/^\/pages\/feeding-101\/?(#.*)?$/.test(href)) a.setAttribute('href', href.replace(/\/pages\/feeding-101\/?/, '/pages/feeding-101?view=feeding-101'));
  });
})();
